import {
  Box,
  Button,
  Link,
  Stack,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
  useDisclosure,
  ModalFooter,
  useColorModeValue,
  Flex,
  Text,
  Progress,
  VStack,
  HStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import smartTruncate from 'smart-truncate';
import ModalComponent from '../../components/Modal';
import { getMyFreezeList, undelegateresource } from '../../common/api';
import { toTron } from '../../common/utils';
import isEmpty from 'is-empty';
import RecentTrades from './RecentTrades';
import Order from './Order/Order';
import { ENERGY, BANDWIDTH, ORDERSTATUS } from '../../common/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { checkAdmin, setMyFreezeList } from '../../redux/accountSlice';
import {
  cancelOrderHandler,
  getUserOrderHandler,
  setIsCancelLoading,
  setIsCancelOpen,
} from '../../redux/orderSlice';
import Sidebar from '../../components/Sidebar';

const Market = () => {
  const dispatch = useDispatch();
  const {
    accountAddress,
    accountBalance,
    totalFrozenBalance,
    accountDetails,
    noWalletDetect,
    tronResources,
    myFreezeList,
    resourceDetails,
  } = useSelector((state) => state.account);
  const { myOpenOrder, isCancelLoading, isCancelOpen } = useSelector(
    (state) => state.order
  );

  const [cancelOrderData, setCancelOrderData] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const toast = useToast();

  const undelegateresourceHandler = async (item) => {
    setIsCancelLoading(true);
    try {
      await undelegateresource(
        item.receiverAddress,
        item.resource,
        item.balance,
        accountAddress
      );
      fetchMyFreezeList();
      toast({
        title: 'Resource Reclaimed Successfully',
        position: 'top-right',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: error || `Something went wrong`,
        position: 'top-right',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCancelLoading(false);
    }
  };


  const fetchMyFreezeList = useCallback(async () => {
    if (isEmpty(accountAddress)) return;
    const _myFreezeList = await getMyFreezeList(accountAddress, 2);
    dispatch(setMyFreezeList(_myFreezeList));
  }, [accountAddress, dispatch]);

  useEffect(() => {
    dispatch(getUserOrderHandler(ORDERSTATUS.PENDING));
    fetchMyFreezeList();
    dispatch(checkAdmin());
  }, [accountAddress, dispatch, fetchMyFreezeList]);

  const cardBg = useColorModeValue('white', '#1a1a1a');
  const mutedText = useColorModeValue('gray.600', 'gray.400');
  const addressBg = useColorModeValue('#f8f9fa', 'gray.800');
  const numberColor = useColorModeValue('gray.800', 'white');
  const progressTrackBg = useColorModeValue('#e9ecef', 'gray.600');

  const handleTabChange = (index) => {
    setActiveTab(index);
    if (index === 2) { // My Open Orders tab
      dispatch(getUserOrderHandler(ORDERSTATUS.PENDING));
    }
  };

  return (
    <>
      {/* <Head
        accountAddress={accountAddress}
        accountBalance={accountBalance}
        totalFrozenBalance={totalFrozenBalance}
        isDataLoading={isDataLoading}
        accountDetails={accountDetails}
      /> */}
      <Flex gap={{ base: 0, lg: '20px' }} direction={{ base: 'column', lg: 'row' }}>
        {/* Show Sidebar on desktop always, on mobile only when wallet not connected */}
        <Box display={{ base: noWalletDetect || !accountAddress ? 'block' : 'none', lg: 'block' }}>
          <Sidebar />
        </Box>
        <Box flex={1} px={{ base: 0, lg: 0 }} py={{ base: 0, lg: 0 }}>
          {/* Mobile Account Overview and Order Form - only show when wallet IS connected */}
          <Box display={{ base: noWalletDetect || !accountAddress ? 'none' : 'block', lg: 'none' }} margin={{ base: '20px 10px', lg: 'auto' }}>
            <VStack spacing={4} mb={6}>
              {/* Account Overview Card for Mobile */}
              <Box bg={cardBg} borderRadius="2xl" boxShadow={useColorModeValue('0 2px 8px rgba(0,0,0,0.08)', 'sm')} w="full" p={6}>
                  <Text fontSize="xl" fontWeight="bold" mb={4} color={numberColor}>Account Overview x</Text>
                  
                  {/* Address */}
                  <Box bg={addressBg} p={3} borderRadius="xl" mb={4}>
                    <Text fontSize="sm" color={mutedText} noOfLines={1}>
                      {accountAddress ? smartTruncate(accountAddress, 35, { position: 11 }) : 'Not connected'}
                    </Text>
                  </Box>
                  
                  {/* Balance Info in Gray Boxes */}
                  <VStack spacing={3} mb={6}>
                    {/* Available Balance Box */}
                    <Box bg={addressBg} p={3} borderRadius="lg" w="full">
                      <HStack justify="space-between" align="center">
                        <Text fontSize="lg" fontWeight="bold" color="red.500">
                          {((accountBalance || 0) + (totalFrozenBalance || 0))?.toFixed(2)} TRX
                        </Text>
                        <Text fontSize="sm" color={mutedText}>
                          / Available {(accountBalance || 0)?.toFixed(2)} TRX
                        </Text>
                      </HStack>
                    </Box>

                    {/* Staked and Vote Available in one row */}
                    <HStack spacing={3} w="full">
                      {/* Staked Box */}
                      <Box bg={addressBg} p={3} borderRadius="lg" flex={1}>
                        <VStack align="start" spacing={1}>
                          <HStack spacing={1}>
                            <Text fontSize="lg" fontWeight="bold" color={numberColor}>
                              {(totalFrozenBalance || 0)?.toFixed(2)}
                            </Text>
                            <Text fontSize="sm" color={mutedText}>TRX</Text>
                          </HStack>
                          <Text fontSize="sm" color={mutedText}>Staked</Text>
                        </VStack>
                      </Box>

                      {/* Vote Available Box */}
                      <Box bg={addressBg} p={3} borderRadius="lg" flex={1}>
                        <VStack align="start" spacing={1}>
                          <HStack spacing={1}>
                            <Text fontSize="lg" fontWeight="bold" color={numberColor}>0</Text>
                            <Text fontSize="sm" color={mutedText}>TRX</Text>
                          </HStack>
                          <Text fontSize="sm" color={mutedText}>Vote Available</Text>
                        </VStack>
                      </Box>
                    </HStack>
                  </VStack>
                  
                  {/* Resources */}
                  <VStack spacing={4} align="stretch">
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text fontSize="md" fontWeight="medium" color={numberColor}>Energy</Text>
                        <Text fontSize="sm" fontWeight="semibold" color={numberColor}>
                          {resourceDetails?.energyRemaining !== undefined
                            ? `${(resourceDetails?.energyRemaining || 0)?.toLocaleString()} / ${(resourceDetails?.energyLimit || 0)?.toLocaleString()}`
                            : '0 / 0'}
                        </Text>
                      </HStack>
                      <Progress
                        value={
                          resourceDetails?.energyRemaining !== undefined &&
                          resourceDetails?.energyLimit !== undefined &&
                          resourceDetails?.energyLimit > 0
                            ? (resourceDetails.energyRemaining / resourceDetails.energyLimit) * 100
                            : 0
                        }
                        size="lg"
                        colorScheme="red"
                        bg={progressTrackBg}
                        borderRadius="full"
                        sx={{
                          '& > div': {
                            background: 'linear-gradient(90deg, #dc3545 0%, #fd7e7e 100%)'
                          }
                        }}
                      />
                    </Box>
                    
                    <Box>
                      <HStack justify="space-between" mb={2}>
                        <Text fontSize="md" fontWeight="medium" color={numberColor}>Bandwidth</Text>
                        <Text fontSize="sm" fontWeight="semibold" color={numberColor}>
                          {resourceDetails?.netRemaining !== undefined
                            ? `${((resourceDetails?.netRemaining || 0) + (resourceDetails?.freeNetRemaining || 0))?.toLocaleString()} / ${((resourceDetails?.netLimit || 0) + (resourceDetails?.freeNetLimit || 0))?.toLocaleString()}`
                            : '0 / 0'}
                        </Text>
                      </HStack>
                      <Progress
                        value={
                          resourceDetails?.netRemaining !== undefined &&
                          resourceDetails?.netLimit !== undefined
                            ? (((resourceDetails?.netRemaining || 0) + (resourceDetails?.freeNetRemaining || 0)) /
                               ((resourceDetails?.netLimit || 0) + (resourceDetails?.freeNetLimit || 0))) * 100
                            : 0
                        }
                        size="lg"
                        colorScheme="red"
                        bg={progressTrackBg}
                        borderRadius="full"
                        sx={{
                          '& > div': {
                            background: 'linear-gradient(90deg, #dc3545 0%, #fd7e7e 100%)'
                          }
                        }}
                      />
                    </Box>
                  </VStack>
              </Box>

              {/* Order Form for Mobile */}
              <Box bg={cardBg} borderRadius="xl" boxShadow="sm" w="full" p={4}>
                  <Order isMobile={true} />
              </Box>
            </VStack>
          </Box>

          {/* {!isAdmin && (
            <Box display='flex' justifyContent='flex-end' mb={5}>
              <Stack direction='row' spacing={4}>
                <Button className='primary' onClick={() => checkWallet()}>
                  Buy
                </Button>
                <Button className='secondary'>Sell</Button>
              </Stack>
            </Box>
          )} */}

          {/* Only for Admin */}

          {/* <OrderComponent /> */}
          {/* <h2 className='title'> Recently Filled Orders</h2> */}
          <Box margin={{ base: '20px 10px', lg: '0' }}>
            <Tabs
              className='tw-card'
              backgroundColor={useColorModeValue('#fff', '#3e3d3c')}
              index={activeTab}
              onChange={handleTabChange}
            >
            <TabList 
              overflowX="auto"
            >
              <Tab>Recent Trades</Tab>
              <Tab>My Delegated List</Tab>
              <Tab>My Open Orders</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Box
                  py={'5'}
                  className='shadow--outset'
                  rounded={'lg'}
                  overflowX={{ base: 'auto', md: 'visible' }}
                >
                  <RecentTrades />
                </Box>
              </TabPanel>

              <TabPanel>
                <Box
                  mt='5'
                  py={'5'}
                  className='shadow--outset'
                  rounded={'lg'}
                  overflowX={{ base: 'auto', md: 'visible' }}
                >
                  {!isEmpty(myFreezeList) ? (
                    <Table variant='unstyled' size='sm' minW={{ base: '600px', md: 'auto' }}>
                      <Thead>
                        <Tr>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Delegated To</Th>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Resource</Th>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Amount</Th>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Expire Date</Th>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Action</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {myFreezeList?.map((item, i) => (
                          <Tr key={i}>
                            <Td>
                              {smartTruncate(item.receiverAddress, 18, {
                                position: 8,
                              })}
                            </Td>
                            <Td>{item.resource == 1 ? ENERGY : BANDWIDTH}</Td>
                            <Td>{item.resourceValue}</Td>
                            <Td>
                              {!isEmpty(item.expireTime)
                                ? moment(item.expireTime).format(
                                    'YYYY-MM-DD HH:mm:ss'
                                  )
                                : '-'}
                            </Td>
                            <Td>
                              <Button
                                className='primary'
                                size={'sm'}
                                mr={5}
                                onClick={() => undelegateresourceHandler(item)}
                                disabled={item.expireTime > Date.now()}
                                isLoading={isCancelLoading}
                              >
                                Reclaim
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  ) : (
                    <div style={{ textAlign: 'center' }}>
                      <h3>No Data Available</h3>
                    </div>
                  )}
                </Box>
              </TabPanel>

              <TabPanel>
                <Box
                  mt='5'
                  py={'5'}
                  className='shadow--outset'
                  rounded={'lg'}
                  overflowX={{ base: 'auto', md: 'visible' }}
                >
                  {!isEmpty(myOpenOrder) ? (
                    <Table variant='unstyled' size='sm' minW={{ base: '700px', md: 'auto' }}>
                      <Thead>
                        <Tr>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Resource</Th>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Price/day</Th>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Payout</Th>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Date</Th>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>TXN ID</Th>
                          <Th whiteSpace={{ base: 'nowrap', md: 'normal' }}>Action</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {myOpenOrder?.map((e) => {
                          return (
                            <Tr key={e.txn_id + e._id}>
                              <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>
                                {e.resource_amount} {e.resource_type}
                              </Td>
                              <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>{e.price_per_day} SUN</Td>
                              <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>{toTron(e.order_amount)} TRX</Td>
                              <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>
                                {moment(e.created_at).format(
                                  'MM-DD HH:mm'
                                )}
                              </Td>
                              <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>
                                <Link
                                  href={`https://tronscan.org/#/transaction/${e.txn_id}`}
                                  isExternal
                                  fontSize="xs"
                                >
                                  {smartTruncate(e.txn_id, 12, { position: 6 })}
                                </Link>
                              </Td>
                              <Td whiteSpace={{ base: 'nowrap', md: 'normal' }}>
                                <Button
                                  size="xs"
                                  colorScheme="red"
                                  onClick={() => {
                                    setCancelOrderData(e);
                                    dispatch(setIsCancelOpen(true));
                                  }}
                                >
                                  Cancel
                                </Button>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  ) : (
                    <div style={{ textAlign: 'center' }}>
                      <h3>No Data Available</h3>
                    </div>
                  )}
                </Box>
              </TabPanel>
            </TabPanels>
            </Tabs>
          </Box>
        </Box>
      </Flex>

      {/* Cancel Order Modal */}
      <ModalComponent
        isOpen={isCancelOpen}
        onClose={() => dispatch(setIsCancelOpen(false))}
        title='Cancel Order'
      >
        <Box className='form_body'>
          <TableContainer>
            <Table variant='unstyled' size='sm'>
              <Tbody>
                <Tr>
                  <Td>Left Energy</Td>
                  <Td>{cancelOrderData.resource_amount}</Td>
                </Tr>
                <Tr>
                  <Td>Return TRX</Td>
                  <Td>{toTron(cancelOrderData.order_amount) - 1} TRX</Td>
                </Tr>
                <Tr>
                  <Td>Cancelation Fee</Td>
                  <Td>1 TRX</Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
          <ModalFooter>
            <Button
              className='primary'
              mr={5}
              onClick={() =>
                dispatch(cancelOrderHandler(cancelOrderData._id))
                  .then((res) => {
                    if (res.status) {
                      dispatch(setIsCancelLoading(false));
                      dispatch(setIsCancelOpen(false));
                      dispatch(getUserOrderHandler(ORDERSTATUS.PENDING));
                      toast({
                        title: res.data.message,
                        position: 'top-right',
                        status: 'success',
                        duration: 5000,
                        isClosable: true,
                      });
                    } else {
                      dispatch(setIsCancelLoading(false));
                      toast({
                        title: `Something went wrong`,
                        position: 'top-right',
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                      });
                    }
                  })
                  .catch((error) => {
                    toast({
                      title: error.message || `Something went wrong`,
                      position: 'top-right',
                      status: 'error',
                      duration: 5000,
                      isClosable: true,
                    });
                  })
              }
              disabled={isCancelLoading}
              isLoading={isCancelLoading}
            >
              Cancel Order
            </Button>
            <Button onClick={() => dispatch(setIsCancelOpen(false))}>
              Close
            </Button>
          </ModalFooter>
        </Box>
      </ModalComponent>
    </>
  );
};

export default Market;
